import styled, { css, keyframes } from 'styled-components';
import { FlexAlignCenter } from 'components/primitives/flex';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { ReactElement, useRef } from 'react';
import { Text } from 'components/primitives/typography';
import { Atom, useAtomValue } from 'jotai';
import { getMaxWidthQuery, getMinWidthQuery, mediaMMdMin, mediaMSmMax } from 'helpers/breakpoints';
import { breakpoints } from '../styleConstants';

export interface CustomFilterButtonProps {
  name: string;
  dropdownActive: boolean;
  disabled?: boolean;
  toggleFilterDropdown: (string) => void;
  isFilterHiddenAtom?: Atom<boolean>;
}
export interface FilterButtonProps extends CustomFilterButtonProps {
  label: string | ReactElement;
  content: string | ReactElement;
  isFilterActiveAtom: Atom<boolean>;
  hideCloseButton?: boolean;
  mobileFullscreen?: boolean;
  extraElementOutsideContent?: ReactElement;
  showCloseCross?: boolean;
}

export default function FilterButton({
  name,
  label,
  isFilterActiveAtom,
  dropdownActive = false,
  content,
  toggleFilterDropdown,
  hideCloseButton = false,
  extraElementOutsideContent,
  mobileFullscreen,
  isFilterHiddenAtom,
  showCloseCross = false,
  disabled = false,
}: FilterButtonProps) {
  const isFilterActive = useAtomValue(isFilterActiveAtom);
  const isFilterHidden = useAtomValue(isFilterHiddenAtom);
  const dropdownRef = useRef();
  const buttonRef = useRef();

  useOnClickOutside(dropdownRef, (e) => {
    if (disabled) {
      return;
    }
    if (buttonRef.current !== e.target) {
      toggleFilterDropdown(null);
    }
  });

  if (isFilterHidden) {
    return null;
  }

  return (
    <Container>
      <Button
        disabled={disabled}
        active={isFilterActive}
        dropdownActive={dropdownActive}
        onClick={() => {
          if (disabled) {
            return;
          }
          toggleFilterDropdown(name);
        }}
        ref={buttonRef}
      >
        {label}
      </Button>
      {dropdownActive && (
        <>
          <DropDownBox
            ref={dropdownRef}
            mobileFullscreen={mobileFullscreen}
            data-testid="search-other-filters-dropdown-box"
          >
            <DropDownContent>
              {showCloseCross && (
                <CloseWrapper onClick={() => toggleFilterDropdown(null)}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15.2559 0.744078C14.9305 0.418641 14.4028 0.418641 14.0774 0.744078L8 6.82149L1.92259 0.744078C1.59715 0.418641 1.06951 0.418641 0.744078 0.744078C0.418641 1.06951 0.418641 1.59715 0.744078 1.92259L6.82149 8L0.744078 14.0774C0.418641 14.4028 0.418641 14.9305 0.744078 15.2559C1.06951 15.5814 1.59715 15.5814 1.92259 15.2559L8 9.17851L14.0774 15.2559C14.4028 15.5814 14.9305 15.5814 15.2559 15.2559C15.5814 14.9305 15.5814 14.4028 15.2559 14.0774L9.17851 8L15.2559 1.92259C15.5814 1.59715 15.5814 1.06951 15.2559 0.744078Z"
                      fill="#8E9697"
                    />
                  </svg>
                </CloseWrapper>
              )}
              <div className="content">{content}</div>
              {!hideCloseButton && <CloseButton onClick={() => toggleFilterDropdown(null)}>Close</CloseButton>}
            </DropDownContent>

            {extraElementOutsideContent}
          </DropDownBox>
          {mobileFullscreen && <MobileFullscreenBackdrop />}
        </>
      )}
    </Container>
  );
}

const CloseWrapper = styled.div`
  position: absolute;
  cursor: pointer;
  right: 48px;
  top: 20px;
  display: none;
  ${mediaMSmMax} {
    display: block;
  }
`;

const Container = styled.div`
  ${mediaMMdMin} {
    position: relative;
  }
`;

const Button = styled(FlexAlignCenter)<{
  dropdownActive: boolean;
  active: boolean;
  disabled: boolean;
}>`
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #ffffff;
  color: #17233c;
  border: 1px solid #dbdfe5;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  margin: auto;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}

  ${({ disabled }) =>
    !disabled &&
    css`
      &:hover {
        background-color: rgba(219, 223, 229, 0.24);
        border: 1px solid #b3b7b8;
      }
    `}



  ${({ dropdownActive }) =>
    dropdownActive &&
    css`
      border: 1px solid #b3b7b8;
      background-color: rgba(219, 223, 229, 0.24);
      // border: 1px solid #17233c;
    `};

  ${({ active, disabled }) =>
    active &&
    css`
      background-color: #eef3fb;
      color: #0751c2;
      border: 1px solid #eef3fb;
      ${!disabled &&
      css`
        &:hover {
          background-color: #eef3fb;
          color: #0751c2;
          border: 1px solid #eef3fb;
        }
      `};
    `};

  ${mediaMMdMin} {
    height: 36px;
  }
`;

const DropDownBox = styled.div<{ mobileFullscreen: boolean }>`
  min-width: min(330px, calc(100vw - 44px));
  max-width: calc(100vw - 44px);
  max-height: 77vh;
  width: max-content;
  overflow-y: auto;
  position: absolute;
  z-index: 1;
  top: 100%;
  background-color: #fff;
  border: 1px solid #dbdfe5;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.25);
  border-radius: 5px;
  overscroll-behavior: contain;

  ${mediaMSmMax} {
    left: 22px;
    top: unset;
  }

  ${({ mobileFullscreen }) =>
    mobileFullscreen &&
    css`
      ${getMaxWidthQuery(breakpoints.MAP_CAN_SHOW - 1)} {
        position: fixed;
        inset: 0;
        left: 0 !important;
        top: 24px !important;
        max-width: unset;
        max-height: unset;
        width: 100%;
        z-index: 100;
        border-radius: 10px 10px 0 0;
        animation: ${slideUp} 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);

        ${DropDownContent} {
          padding: 24px;
          padding-top: 12px;
        }
        ${CloseWrapper} {
          top: 36px;
          right: 26px;
        }
      }
    `};
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0%);
    opacity: 1;
  }
`;

const MobileFullscreenBackdrop = styled.div`
  position: fixed;
  inset: 0;
  backdrop-filter: brightness(60%);

  ${getMinWidthQuery(breakpoints.MAP_CAN_SHOW)} {
    display: none;
  }
`;

const DropDownContent = styled.div`
  padding: 44px;
  position: relative;
`;

const CloseButton = styled(Text)`
  color: #1e53bb;
  font-size: 15px;
  font-weight: 500;
  position: absolute;
  right: 18px;
  bottom: 12px;
  padding: 10px 17px;
  cursor: pointer;
`;
